import React, { FunctionComponent, useCallback, useState } from 'react'
import Dropzone from 'react-dropzone'
import { last, startCase } from 'lodash'
import { Select, t, Trans } from '@lingui/macro'

import Tooltip from 'front-end/src/components/common/tooltip/Tooltip'

import Textarea from '../../../../components/common/form/textarea/Textarea'
import { ButtonPrimary, ButtonLight } from 'front-end/src/components'
import { NbSp } from '../../../../utils/NbSp'
import { useRequiredField } from '../../hooks/useRequiredField'

interface IFileSelectorProps {
  submitFile: (file: File) => void
}

const CustomInputSection: FunctionComponent<IFileSelectorProps> = ({
  submitFile
}) => {
  const [data, setData] = useState<string>('')
  const requiredField = useRequiredField()

  const onSubmit = useCallback(() => {
    const file = new File([data], 'manual_input.csv')
    submitFile(file)
  }, [data, submitFile])

  const placeholder =
    requiredField === 'account_name'
      ? `charles_dickens,Charles Dickens,Sales Manager
will_shakespear,Will Shakespeare,Copywriter
jane_austen,Jane Austen,CEO`
      : `charles.dickens@authors.com,Charles Dickens,Sales Manager
william.shakespeare@authors.com,Will Shakespeare,Copywriter
jane.austen@authors.com,Jane Austen,CEO`

  return (
    <div className="BulkUsersImportPage__section BulkUsersImportPage__upload-csv">
      <h3>
        <Trans>Add details manually</Trans>
      </h3>
      <Textarea onChange={setData} value={data} placeholder={placeholder} />
      <p>
        <Trans>
          Data should be added in comma-separated format, with each user on a
          separate line. Each user must have
        </Trans>
        <NbSp />
        <Select
          value={requiredField}
          account_name="an account name"
          other="an email address"
        />
        .
      </p>
      <Tooltip text={data ? null : t`input some data first`}>
        <ButtonLight disabled={!data} onClick={onSubmit}>
          <Trans>Import manual data</Trans>
        </ButtonLight>
      </Tooltip>
    </div>
  )
}

const UploadFileSection: FunctionComponent<IFileSelectorProps> = ({
  submitFile
}) => {
  const handleDropRejected = useCallback(() => {
    throw new Error('File type is not supported or the file is too big')
  }, [])

  const handleDropAccepted = useCallback(
    ([file]: File[]) => {
      const { name } = file
      const extension = last(name.split('.'))

      if (!extension || extension.toLowerCase() !== 'csv') {
        handleDropRejected()
      }

      submitFile(file)
    },
    [submitFile]
  )

  const requiredField = useRequiredField()

  return (
    <div className="BulkUsersImportPage__section BulkUsersImportPage__upload-csv">
      <h3>
        <Trans>Upload a CSV file</Trans>
      </h3>
      <Dropzone
        onDropAccepted={handleDropAccepted}
        onDropRejected={handleDropRejected}
        multiple={false}
        maxSize={1073741824}
      >
        {({ getRootProps, getInputProps, open }) => (
          <>
            <div
              className="BulkUsersImportPage__dropzone"
              style={{
                position: 'inherit',
                width: 'auto',
                height: 'auto'
              }}
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              <p>
                <Trans>
                  User details must be uploaded in a CSV format. This is a
                  widely supported data-sharing format, and all major
                  spreadsheet applications will allow you to save files in this
                  format.
                </Trans>
              </p>
              <p>
                <Trans>
                  Your file should contain a single row for each user you wish
                  to add/update. Every user (row) MUST have an{' '}
                  {startCase(requiredField)} listed.
                </Trans>
              </p>
            </div>
            <ButtonPrimary
              onClick={() => {
                open()
              }}
            >
              <Trans>Select a file to upload</Trans>
            </ButtonPrimary>
          </>
        )}
      </Dropzone>
    </div>
  )
}

export const SelectDataSource: FunctionComponent<IFileSelectorProps> = ({
  submitFile
}) => {
  const requiredField = useRequiredField()

  return (
    <div className="BulkUsersImportPage__step data-step">
      <div className="row">
        <UploadFileSection submitFile={submitFile} />
        <div className="BulkUsersImportPage__or-separator">
          <Trans>OR</Trans>
        </div>
        <CustomInputSection submitFile={submitFile} />
      </div>
      <div className="BulkUsersImportPage__data-fields-help">
        <p>
          <Trans>
            Looop currently supports the following user data fields:
          </Trans>
        </p>
        <ul>
          <li>
            {startCase(requiredField)} (<Trans>required</Trans>)
          </li>
          <li>
            <Trans>First name</Trans>
          </li>
          <li>
            <Trans>Last name</Trans>
          </li>
          <li>
            <Trans>Display name</Trans>
          </li>
          <li>
            <Trans>Group or multiple Groups (separated by semicolon)</Trans>
          </li>
          <li>
            <Trans>Employee ID</Trans>
          </li>
          <li>
            <Trans>Position</Trans>
          </li>
          <li>
            <Trans>Area</Trans>
          </li>
          <li>
            <Trans>Department</Trans>
          </li>
          <li>
            <Trans>Location</Trans>
          </li>
          <li>
            <Trans>Manager email</Trans>
          </li>
        </ul>
        <p>
          <Trans>
            We also allow up to 3 custom fields to be added to each user.
          </Trans>
        </p>
      </div>
    </div>
  )
}
